import { ExportProgressWrapper } from '@/components/commonComponents/style';
import {
  ExportDropdownCount,
  ExportDropDownItem,
} from '@/components/ExportDropdown/styles';
import { AnalysisStatus } from '@/enums';
import { useRowSelectionTable, useToggleModal } from '@/hooks';
import { AnalysisEntity, FileData } from '@/interfaces';
import { AnalysisSampleKitEntity } from '@/types';
import {
  CustomButton,
  CustomDropdown,
  CustomModal,
  Icon,
} from '@amili-v3/amili-v3-design';
import { UseMutationResult } from '@tanstack/react-query';
import { MenuProps, notification, Progress } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const streamDownloadSampleKitReports =
  'streamProcessingDownloadSampleKits';

type DownloadSampleKitDropdownProps = {
  rowSelectionValues: ReturnType<typeof useRowSelectionTable>;
  analysisBatchDetail: AnalysisEntity | undefined;
  downloadAllSampleKitsMutationResult: UseMutationResult<
    { success: FileData[]; failed: string[] },
    Error,
    {
      fileNameZip: string;
      analysisBatchId?: string;
      kitIds?: string[];
    },
    unknown
  >;
  onLoadingSampleKitTable: (loading: boolean) => void;
};
const DownloadSampleKitDropdown: FC<DownloadSampleKitDropdownProps> = props => {
  const {
    rowSelectionValues,
    analysisBatchDetail,
    downloadAllSampleKitsMutationResult,
    onLoadingSampleKitTable,
  } = props;
  const { t } = useTranslation();
  const { mutate: downloadAllSamplesMutate, isPending: isLoadingDownload } =
    downloadAllSampleKitsMutationResult;

  useEffect(() => {
    onLoadingSampleKitTable(isLoadingDownload);
  }, [isLoadingDownload, onLoadingSampleKitTable]);

  const { selectedCount, records: currentSampleKitsRecords } =
    rowSelectionValues;

  const [percentProgress, setPercentProgress] = useState(0);
  const [isDownloadAllSampleKits, setDownloadAllSampleKits] = useState(false);
  const { loadingDownloadModal } = useToggleModal(['loadingDownload']);

  // Handle quantity of analysis sample kits signal
  useEffect(() => {
    const handler = (event: Event) => {
      const e = event as CustomEvent;
      setPercentProgress(e.detail?.percentProgress ?? 0);
    };

    window.addEventListener(streamDownloadSampleKitReports, handler);

    return () =>
      window.removeEventListener(streamDownloadSampleKitReports, handler);
  }, []);

  const handleClose = () => {
    setPercentProgress(0);
    setDownloadAllSampleKits(false);
  };

  const handleDownloadSampleKits = useCallback(
    (isDownloadAll: boolean) => {
      const downloadPayload = {
        fileNameZip: `${analysisBatchDetail?.displayId?.toUpperCase()}_REPORT`,
        analysisBatchId: isDownloadAll
          ? analysisBatchDetail?.displayId
          : undefined,
        kitIds: isDownloadAll
          ? undefined
          : currentSampleKitsRecords.map(
              (record: AnalysisSampleKitEntity) =>
                record?.kit?.serial?.toLowerCase() ?? '',
            ),
      };

      if (isDownloadAll) {
        setDownloadAllSampleKits(true);
        loadingDownloadModal.toggle();
      }
      downloadAllSamplesMutate(downloadPayload, {
        onSuccess: handleClose,
        onError: error => notification.error({ message: error.message }),
      });
    },
    [
      analysisBatchDetail?.displayId,
      currentSampleKitsRecords,
      downloadAllSamplesMutate,
      loadingDownloadModal,
    ],
  );

  const isCompletedBatch =
    analysisBatchDetail?.status === AnalysisStatus.ANALYSIS_COMPLETED;
  const dropdownItems: MenuProps['items'] = [
    {
      key: 'download-all-sample-kits',
      onClick: () => handleDownloadSampleKits(true),
      label: (
        <ExportDropDownItem>
          <ExportDropdownCount>{`(${selectedCount})`}</ExportDropdownCount>
          {t('common.all')}
        </ExportDropDownItem>
      ),
    },
    {
      key: 'download-current-sample-kits',
      onClick: () => handleDownloadSampleKits(false),
      label: (
        <ExportDropDownItem>
          <ExportDropdownCount>{`(${currentSampleKitsRecords.length})`}</ExportDropdownCount>
          {t('common.currentPage')}
        </ExportDropDownItem>
      ),
    },
  ];

  return (
    <>
      <CustomDropdown
        placement="bottom"
        menu={{ items: dropdownItems }}
        disabled={!isCompletedBatch || isLoadingDownload}
      >
        <CustomButton
          key="download"
          type="white"
          prefix={<Icon name="download" />}
          suffix={<Icon name="chevronDown" />}
        >
          {t('common.download')}
        </CustomButton>
      </CustomDropdown>

      <CustomModal
        centered
        title="Loading Download Reports"
        open={percentProgress > 0 && isDownloadAllSampleKits}
        onCancel={handleClose}
        footer={null}
      >
        <ExportProgressWrapper style={{ maxHeight: 100 }}>
          <Progress percent={percentProgress} />
        </ExportProgressWrapper>
      </CustomModal>
    </>
  );
};

export default memo(DownloadSampleKitDropdown);
